import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { invoiceService } from '../services/api';

const COLORS = [
  '#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#a4de6c',
  '#d0ed57', '#83a6ed', '#8dd1e1', '#a4262c', '#ec407a',
  '#7c4dff', '#00bcd4'
];

const SERVERLESS_KEYWORDS = [
  'serverless inference',
  'genai',
  'gen ai',
  'generative ai',
  'inference',
];

function isServerlessInference(item) {
  const searchable = [item.product, item.resourceName, item.description, item.resourceId]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return SERVERLESS_KEYWORDS.some(kw => searchable.includes(kw));
}

// Extract the API key name from resourceName (group_description)
function extractKeyName(item) {
  return item.resourceName || item.product || 'Unknown Key';
}

// Extract the model name from description
function extractModelName(item) {
  const desc = item.description;
  if (!desc) return 'Unknown Model';

  // Common patterns: "Serverless Inference - llama3-70b-instruct"
  const dashMatch = desc.match(
    /(?:serverless inference|genai|inference)\s*[-–:]\s*(.+)/i
  );
  if (dashMatch) return dashMatch[1].trim();

  // If description contains a slash it's probably a model path
  if (desc.match(/\//)) return desc.trim();

  return desc.trim() || 'Unknown Model';
}

function aggregateData(invoicesData) {
  const now = new Date();
  const currentPeriod = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  const monthsSet = new Set();
  const keysSet = new Set();
  const modelsSet = new Set();

  // Granular: key + model + month
  const granularMap = new Map();
  // Aggregated by key + month
  const keyMonthMap = new Map();
  // Aggregated by model + month
  const modelMonthMap = new Map();

  const keyTotalCost = new Map();
  const modelTotalCost = new Map();

  for (const { invoice, items } of invoicesData) {
    const month = invoice.period.slice(0, 7);

    for (const item of items) {
      if (!isServerlessInference(item)) continue;

      const keyName = extractKeyName(item);
      const modelName = extractModelName(item);
      const cost = parseFloat(item.USD) || 0;
      const isCurrentMonth = month === currentPeriod;

      monthsSet.add(month);
      keysSet.add(keyName);
      modelsSet.add(modelName);

      // Granular
      const gKey = `${month}|${keyName}|${modelName}`;
      const gExisting = granularMap.get(gKey);
      if (gExisting) {
        gExisting.costUsd += cost;
      } else {
        granularMap.set(gKey, { keyName, modelName, month, costUsd: cost, isCurrentMonth });
      }

      // By key + month
      const kmKey = `${month}|${keyName}`;
      const kmExisting = keyMonthMap.get(kmKey);
      if (kmExisting) {
        kmExisting.costUsd += cost;
      } else {
        keyMonthMap.set(kmKey, { name: keyName, month, costUsd: cost, isCurrentMonth });
      }

      // By model + month
      const mmKey = `${month}|${modelName}`;
      const mmExisting = modelMonthMap.get(mmKey);
      if (mmExisting) {
        mmExisting.costUsd += cost;
      } else {
        modelMonthMap.set(mmKey, { name: modelName, month, costUsd: cost, isCurrentMonth });
      }

      keyTotalCost.set(keyName, (keyTotalCost.get(keyName) ?? 0) + cost);
      modelTotalCost.set(modelName, (modelTotalCost.get(modelName) ?? 0) + cost);
    }
  }

  const granular = Array.from(granularMap.values());
  const byKey = Array.from(keyMonthMap.values());
  const byModel = Array.from(modelMonthMap.values());
  const totalSpend = byKey.reduce((sum, u) => sum + u.costUsd, 0);
  const currentMonthSpend = byKey
    .filter(u => u.isCurrentMonth)
    .reduce((sum, u) => sum + u.costUsd, 0);

  let topModel = null;
  let topCost = 0;
  for (const [model, cost] of modelTotalCost) {
    if (cost > topCost) {
      topModel = model;
      topCost = cost;
    }
  }

  const months = Array.from(monthsSet).sort();
  const keys = Array.from(keysSet).sort();
  const models = Array.from(modelsSet).sort();

  return {
    granular, byKey, byModel,
    totalSpend, currentMonthSpend, topModel,
    months, keys, models,
  };
}

const formatCurrency = (value) => {
  if (value === undefined || value === null) return '$0.00';
  return `$${Number(value).toFixed(2)}`;
};

const formatMonth = (monthStr) => {
  if (!monthStr) return '';
  const [year, month] = monthStr.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1, 1);
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

const ChevronIcon = ({ expanded }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={`h-4 w-4 transition-transform ${expanded ? 'rotate-90' : ''}`}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const ServerlessInferenceCosts = () => {
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState('');
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [viewMode, setViewMode] = useState('key'); // 'key' or 'model'
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [sortConfig, setSortConfig] = useState({ key: 'month', direction: 'desc' });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setLoadingProgress('Fetching billing periods...');

      const billingPeriods = await invoiceService.getBillingPeriods();

      if (!billingPeriods || billingPeriods.length === 0) {
        setError('No billing periods found.');
        setLoading(false);
        return;
      }

      const invoicesData = [];
      for (let i = 0; i < billingPeriods.length; i++) {
        const bp = billingPeriods[i];
        setLoadingProgress(`Loading invoice ${i + 1} of ${billingPeriods.length}...`);

        try {
          const items = await invoiceService.getInvoiceData(bp.id);
          invoicesData.push({ invoice: bp, items });
        } catch (err) {
          console.warn(`Failed to fetch invoice ${bp.id}:`, err);
        }
      }

      const result = aggregateData(invoicesData);
      setData(result);
    } catch (err) {
      console.error('Error fetching inference data:', err);
      setError('Failed to fetch invoice data. Please try again.');
    } finally {
      setLoading(false);
      setLoadingProgress('');
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleExpanded = (rowKey) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    setExpandedRows(new Set());
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return ' \u2195';
    return sortConfig.direction === 'asc' ? ' \u2191' : ' \u2193';
  };

  if (loading) {
    return (
      <div className="bg-white border border-cf-gray-200 rounded-cf-md p-8 shadow-cf text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-do-blue mx-auto mb-4"></div>
        <p className="text-sm text-cf-gray-600">{loadingProgress || 'Loading...'}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white border border-cf-gray-200 rounded-cf-md p-6 shadow-cf text-center">
        <p className="text-sm text-red-600 mb-4">{error}</p>
        <button
          onClick={fetchData}
          className="px-4 py-2 bg-do-blue text-white rounded-cf text-sm font-medium hover:bg-do-blue-dark transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!data || data.byKey.length === 0) {
    return (
      <div className="bg-white border border-cf-gray-200 rounded-cf-md p-6 shadow-cf text-center">
        <p className="text-sm text-cf-gray-600">No serverless inference usage found across your invoices.</p>
      </div>
    );
  }

  const { granular, byKey, byModel, totalSpend, currentMonthSpend, topModel, months, keys, models } = data;

  // Chart uses the primary grouping dimension
  const chartPrimaryItems = viewMode === 'key' ? keys : models;
  const chartRows = viewMode === 'key' ? byKey : byModel;

  const chartData = months.map(month => {
    const entry = { month: formatMonth(month) };
    chartPrimaryItems.forEach(name => {
      const usage = chartRows.find(u => u.month === month && u.name === name);
      entry[name] = usage ? parseFloat(usage.costUsd.toFixed(2)) : 0;
    });
    return entry;
  });

  // Table rows: primary grouped rows, sorted
  const primaryRows = [...chartRows].sort((a, b) => {
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    if (sortConfig.key === 'month') return dir * a.month.localeCompare(b.month);
    if (sortConfig.key === 'name') return dir * a.name.localeCompare(b.name);
    if (sortConfig.key === 'cost') return dir * (a.costUsd - b.costUsd);
    return 0;
  });

  // For each primary row, find the detail rows
  const getDetailRows = (primaryName, month) => {
    return granular
      .filter(g => {
        if (viewMode === 'key') return g.keyName === primaryName && g.month === month;
        return g.modelName === primaryName && g.month === month;
      })
      .sort((a, b) => b.costUsd - a.costUsd);
  };

  const primaryLabel = viewMode === 'key' ? 'Key' : 'Model';
  const detailLabel = viewMode === 'key' ? 'Model' : 'Key';

  return (
    <div>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-cf-gray-200 rounded-cf-md p-4 shadow-cf">
          <p className="text-xs font-medium text-cf-gray-500 uppercase tracking-wider mb-1">Total Spend</p>
          <p className="text-2xl font-semibold text-cf-gray-900">{formatCurrency(totalSpend)}</p>
          <p className="text-xs text-cf-gray-500 mt-1">Across all invoices</p>
        </div>
        <div className="bg-white border border-cf-gray-200 rounded-cf-md p-4 shadow-cf">
          <p className="text-xs font-medium text-cf-gray-500 uppercase tracking-wider mb-1">Current Month</p>
          <p className="text-2xl font-semibold text-cf-gray-900">{formatCurrency(currentMonthSpend)}</p>
          <p className="text-xs text-cf-gray-500 mt-1">Month to date</p>
        </div>
        <div className="bg-white border border-cf-gray-200 rounded-cf-md p-4 shadow-cf">
          <p className="text-xs font-medium text-cf-gray-500 uppercase tracking-wider mb-1">Top Model</p>
          <p className="text-lg font-semibold text-cf-gray-900 truncate" title={topModel || 'N/A'}>
            {topModel || 'N/A'}
          </p>
          <p className="text-xs text-cf-gray-500 mt-1">By total spend</p>
        </div>
      </div>

      {/* View Mode Toggle */}
      <div className="flex items-center gap-2 mb-4">
        <span className="text-sm text-cf-gray-600">Group by:</span>
        <div className="inline-flex rounded-cf border border-cf-gray-300 overflow-hidden">
          <button
            onClick={() => handleViewModeChange('key')}
            className={`px-3 py-1.5 text-sm font-medium transition-colors ${
              viewMode === 'key'
                ? 'bg-do-blue-50 text-do-blue-700 border-r border-cf-gray-300'
                : 'bg-white text-cf-gray-700 hover:bg-cf-gray-50 border-r border-cf-gray-300'
            }`}
          >
            API Key
          </button>
          <button
            onClick={() => handleViewModeChange('model')}
            className={`px-3 py-1.5 text-sm font-medium transition-colors ${
              viewMode === 'model'
                ? 'bg-do-blue-50 text-do-blue-700'
                : 'bg-white text-cf-gray-700 hover:bg-cf-gray-50'
            }`}
          >
            Model
          </button>
        </div>
      </div>

      {/* Stacked Bar Chart */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-2 text-cf-gray-900">
          Cost by {primaryLabel} per Month
        </h2>
        <div className="bg-white border border-cf-gray-200 p-4 rounded-cf-md shadow-cf">
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E8" />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 12, fill: '#6B6B6B' }}
                stroke="#E8E8E8"
              />
              <YAxis
                label={{ value: 'Cost (USD)', angle: -90, position: 'insideLeft', fill: '#6B6B6B' }}
                tick={{ fontSize: 12, fill: '#6B6B6B' }}
                stroke="#E8E8E8"
                tickFormatter={(value) => `$${value}`}
              />
              <Tooltip
                formatter={(value) => formatCurrency(value)}
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #E8E8E8',
                  borderRadius: '3px',
                  fontSize: '12px'
                }}
              />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              {chartPrimaryItems.map((name, index) => (
                <Bar
                  key={name}
                  dataKey={name}
                  stackId="a"
                  fill={COLORS[index % COLORS.length]}
                  name={name}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Breakdown Table */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-2 text-cf-gray-900">Monthly Breakdown</h2>
        <div className="overflow-x-auto bg-white border border-cf-gray-200 rounded-cf-md shadow-cf">
          <table className="min-w-full">
            <thead className="bg-cf-gray-50 border-b border-cf-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-cf-gray-700 uppercase tracking-wider w-8"></th>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-cf-gray-700 uppercase tracking-wider cursor-pointer hover:bg-cf-gray-100 select-none"
                  onClick={() => handleSort('month')}
                >
                  Month{getSortIndicator('month')}
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-cf-gray-700 uppercase tracking-wider cursor-pointer hover:bg-cf-gray-100 select-none"
                  onClick={() => handleSort('name')}
                >
                  {primaryLabel}{getSortIndicator('name')}
                </th>
                <th
                  className="px-4 py-3 text-right text-xs font-semibold text-cf-gray-700 uppercase tracking-wider cursor-pointer hover:bg-cf-gray-100 select-none"
                  onClick={() => handleSort('cost')}
                >
                  Cost{getSortIndicator('cost')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cf-gray-200">
              {primaryRows.map((row) => {
                const rowKey = `${row.month}|${row.name}`;
                const isExpanded = expandedRows.has(rowKey);
                const details = getDetailRows(row.name, row.month);
                const hasDetails = details.length > 0;
                const nameIndex = chartPrimaryItems.indexOf(row.name);
                const color = COLORS[nameIndex % COLORS.length];

                return (
                  <React.Fragment key={rowKey}>
                    <tr
                      className={`hover:bg-cf-gray-50 transition-colors ${hasDetails ? 'cursor-pointer' : ''}`}
                      onClick={() => hasDetails && toggleExpanded(rowKey)}
                    >
                      <td className="pl-4 pr-1 py-3 text-cf-gray-400">
                        {hasDetails && <ChevronIcon expanded={isExpanded} />}
                      </td>
                      <td className="px-4 py-3 text-sm text-cf-gray-800">{formatMonth(row.month)}</td>
                      <td className="px-4 py-3 text-sm text-cf-gray-800">
                        <span className="inline-flex items-center gap-2">
                          <span
                            className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: color }}
                          />
                          {row.name}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-cf-gray-800 font-medium">
                        {formatCurrency(row.costUsd)}
                      </td>
                    </tr>
                    {isExpanded && details.map((detail) => {
                      const detailName = viewMode === 'key' ? detail.modelName : detail.keyName;
                      return (
                        <tr
                          key={`${rowKey}|${detailName}`}
                          className="bg-cf-gray-50"
                        >
                          <td className="pl-4 pr-1 py-2"></td>
                          <td className="px-4 py-2"></td>
                          <td className="px-4 py-2 text-sm text-cf-gray-600">
                            <span className="inline-flex items-center gap-2 pl-5">
                              <span className="text-xs text-cf-gray-400">{detailLabel}:</span>
                              {detailName}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-sm text-right text-cf-gray-600">
                            {formatCurrency(detail.costUsd)}
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
              <tr className="bg-cf-gray-50 font-semibold border-t-2 border-cf-gray-300">
                <td className="px-4 py-3"></td>
                <td className="px-4 py-3 text-sm text-cf-gray-900" colSpan={2}>Total</td>
                <td className="px-4 py-3 text-sm text-right text-cf-gray-900">
                  {formatCurrency(totalSpend)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ServerlessInferenceCosts;
